import { useState, useRef } from 'react'
import { Search, Play, Pause, X } from 'lucide-react'
import type { VoiceProvider } from '@/stores/personaStore'
import { VoiceBrowserModal } from './VoiceBrowserModal'
import type { BrowsableVoice } from './types'

// Providers that have a voice browser adapter
const BROWSABLE_PROVIDERS: VoiceProvider[] = ['elevenlabs', 'kokoro']

interface VoiceIdFieldProps {
  provider: VoiceProvider
  voiceId: string
  voiceName?: string
  voicePreviewUrl?: string
  onChange: (voiceId: string, voiceName?: string, voicePreviewUrl?: string, supportedModels?: string[]) => void
  hasApiKey: boolean
  error?: string
}

export function VoiceIdField({
  provider,
  voiceId,
  voiceName,
  voicePreviewUrl,
  onChange,
  hasApiKey,
  error,
}: VoiceIdFieldProps) {
  const [modalOpen, setModalOpen] = useState(false)
  const [manualMode, setManualMode] = useState(false)
  const [isPlayingChip, setIsPlayingChip] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const isBrowsable = BROWSABLE_PROVIDERS.includes(provider)
  const hasBrowsedVoice = voiceName && voiceId && !manualMode

  // If provider isn't browsable, show plain text input (current behavior)
  if (!isBrowsable) {
    return (
      <input
        type="text"
        value={voiceId}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter voice ID"
        className={`w-full bg-background border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-hidden focus:border-primary ${
          error ? 'border-destructive' : 'border-border'
        }`}
      />
    )
  }

  const handleVoiceSelected = (voice: BrowsableVoice) => {
    const models = (voice.provider_metadata?.supported_models as string[] | undefined) ?? undefined
    onChange(voice.voice_id, voice.name, voice.preview_url ?? undefined, models)
    setManualMode(false)
  }

  const handleClear = () => {
    onChange('', undefined, undefined, undefined)
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current = null
    }
    setIsPlayingChip(false)
  }

  const handlePlayChip = () => {
    if (!voicePreviewUrl) return

    if (isPlayingChip && audioRef.current) {
      audioRef.current.pause()
      audioRef.current = null
      setIsPlayingChip(false)
      return
    }

    const audio = new Audio(voicePreviewUrl)
    audioRef.current = audio
    setIsPlayingChip(true)
    audio.addEventListener('ended', () => {
      setIsPlayingChip(false)
      audioRef.current = null
    })
    audio.play()
  }

  return (
    <>
      <div className="space-y-2">
        {/* Browsed voice chip (shown when selected from browser) */}
        {hasBrowsedVoice && (
          <div className="flex items-center gap-2 p-2 bg-primary/10 border border-primary/50 rounded-md">
            <button
              onClick={handlePlayChip}
              disabled={!voicePreviewUrl}
              className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                isPlayingChip
                  ? 'bg-primary text-primary-foreground'
                  : voicePreviewUrl
                    ? 'bg-muted text-foreground hover:bg-muted-foreground/10'
                    : 'bg-muted/60 text-muted-foreground'
              }`}
            >
              {isPlayingChip ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3 ml-0.5" />}
            </button>
            <div className="flex-1 min-w-0">
              <span className="text-sm text-foreground font-medium">{voiceName}</span>
              <span className="text-xs text-muted-foreground ml-2">{voiceId}</span>
            </div>
            <button
              onClick={handleClear}
              className="text-muted-foreground hover:text-foreground p-1"
              title="Clear selection"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Manual text input (shown in manual mode) */}
        {manualMode && (
          <input
            type="text"
            value={voiceId}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Enter voice ID"
            className={`w-full bg-background border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-hidden focus:border-primary ${
              error ? 'border-destructive' : 'border-border'
            }`}
          />
        )}

        {/* Empty state (no voice selected and not in manual mode) */}
        {!hasBrowsedVoice && !manualMode && !voiceId && (
          <div className="text-xs text-muted-foreground py-1">No voice selected</div>
        )}

        {/* If there's a voiceId but no voiceName and not in manual mode, show as manual */}
        {!hasBrowsedVoice && !manualMode && voiceId && (
          <div className="flex items-center gap-2 p-2 bg-muted border border-border rounded-md">
            <span className="text-sm text-foreground truncate flex-1">{voiceId}</span>
            <button onClick={handleClear} className="text-muted-foreground hover:text-foreground p-1">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-2">
          <button
            onClick={() => setModalOpen(true)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs transition-colors ${
              hasBrowsedVoice
                ? 'bg-muted text-muted-foreground hover:text-foreground'
                : 'bg-primary text-primary-foreground hover:bg-primary/90'
            }`}
          >
            <Search className="w-3.5 h-3.5" />
            {hasBrowsedVoice ? 'Change' : 'Browse Voices'}
          </button>
          <button
            onClick={() => {
              setManualMode(!manualMode)
            }}
            className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${
              manualMode
                ? 'border-primary text-primary'
                : 'border-border text-muted-foreground hover:text-foreground'
            }`}
          >
            {manualMode ? 'Using ID' : 'Enter ID manually'}
          </button>
        </div>

        {error && <p className="text-xs text-destructive">{error}</p>}
      </div>

      <VoiceBrowserModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        provider={provider}
        hasApiKey={hasApiKey}
        onSelect={handleVoiceSelected}
      />
    </>
  )
}
